import { apiClient } from '../services/api';
import { ArchiveDoc, ContractFields } from '../types/archive';
import raw from './archiveDocs.json';

/**
 * 아카이브. **2026-09-28 부터 실제 DB 를 본다** — `USE_MOCK = false`.
 * 드라이브에서 들인 과거 계약서(추출이 끝난 것)가 온다. 목으로 돌리려면 true 로.
 */
export const USE_MOCK = false;

// 목일 때만 쓴다. 새로고침하면 처음으로 돌아간다
let store: ArchiveDoc[] = JSON.parse(JSON.stringify(raw.items));

const delay = <T,>(v: T): Promise<T> =>
  new Promise((resolve) => setTimeout(() => resolve(v), 120));

export const archiveApi = {
  /** 최신순. 같은 달이면 나중에 들어온 것이 위로 */
  list(): Promise<ArchiveDoc[]> {
    if (!USE_MOCK) return apiClient.get('/archive/documents');
    return delay(
      [...store].sort((a, b) => b.docMonth.localeCompare(a.docMonth) || b.id - a.id),
    );
  },

  /** 사람이 확인했다고 표시한다. 검수된 문서는 다시 추출해도 덮지 않는다 */
  markReviewed(id: number, reviewed: boolean): Promise<ArchiveDoc> {
    if (!USE_MOCK) return apiClient.patch(`/archive/documents/${id}/review`, { reviewed });
    store = store.map((d) => (d.id === id ? { ...d, reviewed } : d));
    return delay(store.find((d) => d.id === id)!);
  },

  /** 검수 화면에서 계약 값을 고친다. 고치면 서버가 검수로 표시한다(다시 추출해도 안 덮인다) */
  /** 문서 공통 값 — 제목 · 태그 · 날짜(계약서가 아닌 문서도, #111). 고치면 검수로 표시된다 */
  updateDocument(id: number, patch: { title?: string; keywords?: string[]; docDate?: string | null }): Promise<ArchiveDoc> {
    return apiClient.patch(`/archive/documents/${id}`, patch);
  },

  updateContract(id: number, patch: Partial<ContractFields>): Promise<ArchiveDoc> {
    if (!USE_MOCK) return apiClient.patch(`/archive/documents/${id}/contract`, patch);
    store = store.map((d) => (d.id === id ? { ...d, reviewed: true, contract: { ...d.contract!, ...patch } } : d));
    return delay(store.find((d) => d.id === id)!);
  },

  /** 원본 파일. 서버가 드라이브에서 읽기 전용으로 받아 넘긴다. download 면 형식과 상관없이 내려받기용 */
  file(id: number, download = false): Promise<Blob> {
    return apiClient.getBlob(`/archive/documents/${id}/file${download ? '?download=1' : ''}`);
  },

  /** 연결 안 된 사업 폴더 + 이을 수 있는 프로젝트. 동기화가 목록을 새로 쓴다 */
  unlinkedFolders(): Promise<UnlinkedFolders> {
    if (!USE_MOCK) return apiClient.get('/archive/folders/unlinked');
    return delay({ syncedAt: null, folders: [], candidates: [] });
  },

  /** 폴더를 기존 프로젝트(드라이브 폴더 없는 것)에 잇는다. 폴더 안 미연결 문서도 붙는다 */
  linkFolder(folderId: string, projectId: number): Promise<{ projectId: number; docs: number }> {
    return apiClient.post(`/archive/folders/${encodeURIComponent(folderId)}/link`, { projectId });
  },

  /** 폴더로 프로젝트를 만든다 — 사람이 눌렀을 때만 */
  createFolderProject(folderId: string): Promise<{ projectId: number; docs: number }> {
    return apiClient.post(`/archive/folders/${encodeURIComponent(folderId)}/project`);
  },

  /**
   * 드라이브 → 아카이브 동기화. 매일 05:00 크론과 같은 일이다. 드라이브는 읽기만 한다.
   * 새 사업 폴더는 프로젝트를 만들지 않고 `unlinkedFolders` 로 돌려준다
   */
  sync(): Promise<DriveSyncResult> {
    if (!USE_MOCK) return apiClient.post('/archive/sync');
    return delay({ docs: store.length, inserted: 0, removed: 0, unlinkedFolders: [], seconds: 0 });
  },
};

export interface DriveSyncResult {
  docs: number;
  inserted: number;
  removed: number;
  unlinkedFolders: { folderId: string }[];
  seconds: number;
}

export interface UnlinkedFolders {
  syncedAt: string | null;
  folders: { folderId: string; folderPath: string; name: string; year: number; docs: number }[];
  candidates: { id: number; name: string; clientName: string; stage: string }[];
}
